/**
 * Tangente glissante — logique pure (Boîte à outils, 1re → Tle).
 *
 * Un point A d'abscisse a sur la courbe de f ; B d'abscisse a + h. La pente
 * de la sécante (AB), le taux d'accroissement (f(a + h) − f(a))/h, tend vers
 * f′(a) quand h tend vers 0 : c'est la pente de la tangente en A, d'équation
 * y = f′(a)(x − a) + f(a). Valeurs exactes quand a s'y prête (fractions,
 * racines), approchées sinon.
 */

import type { Viewport } from '../../ui/frame';
import { formatNumber, superscript } from '../../ui/format';

export type FnId = 'square' | 'cubic' | 'inverse' | 'sqrt' | 'exp' | 'ln';

export interface Fn {
  id: FnId;
  /** puce */
  label: string;
  /** « x² », « x³ − 3x » */
  text: string;
  /** « 2x », « 3x² − 3 » */
  derivText: string;
  f: (x: number) => number;
  d: (x: number) => number;
  /** a permis (f dérivable en a) */
  ok: (a: number) => boolean;
  /** bornes du point A */
  aMin: number;
  aMax: number;
  aStart: number;
  window: Viewport;
  minRank: number;
}

export const FUNCTIONS: readonly Fn[] = [
  {
    id: 'square',
    label: 'x²',
    text: 'x²',
    derivText: '2x',
    f: (x) => x * x,
    d: (x) => 2 * x,
    ok: () => true,
    aMin: -2,
    aMax: 2,
    aStart: 1,
    window: { xMin: -3, xMax: 3, yMin: -1, yMax: 5 },
    minRank: 6,
  },
  {
    id: 'cubic',
    label: 'x³ − 3x',
    text: 'x³ − 3x',
    derivText: '3x² − 3',
    f: (x) => x * x * x - 3 * x,
    d: (x) => 3 * x * x - 3,
    ok: () => true,
    aMin: -2,
    aMax: 2,
    aStart: -1.5,
    window: { xMin: -3, xMax: 3, yMin: -3, yMax: 3 },
    minRank: 6,
  },
  {
    id: 'inverse',
    label: '1/x',
    text: '1/x',
    derivText: '−1/x²',
    f: (x) => 1 / x,
    d: (x) => -1 / (x * x),
    ok: (a) => Math.abs(a) >= 0.3 - 1e-9,
    aMin: -3.5,
    aMax: 3.5,
    aStart: 1,
    window: { xMin: -4, xMax: 4, yMin: -4, yMax: 4 },
    minRank: 6,
  },
  {
    id: 'sqrt',
    label: '√x',
    text: '√x',
    derivText: '1/(2√x)',
    f: (x) => (x < 0 ? NaN : Math.sqrt(x)),
    d: (x) => 1 / (2 * Math.sqrt(x)),
    ok: (a) => a > 0,
    aMin: 0.25,
    aMax: 6.5,
    aStart: 1,
    window: { xMin: -1, xMax: 7, yMin: -2, yMax: 6 },
    minRank: 6,
  },
  {
    id: 'exp',
    label: 'eˣ',
    text: 'eˣ',
    derivText: 'eˣ',
    f: Math.exp,
    d: Math.exp,
    ok: () => true,
    aMin: -3.5,
    aMax: 1.5,
    aStart: 0,
    window: { xMin: -4, xMax: 2, yMin: -1, yMax: 5 },
    minRank: 6,
  },
  {
    id: 'ln',
    label: 'ln x',
    text: 'ln x',
    derivText: '1/x',
    f: (x) => (x > 0 ? Math.log(x) : NaN),
    d: (x) => 1 / x,
    ok: (a) => a > 0,
    aMin: 0.25,
    aMax: 6.5,
    aStart: 1,
    window: { xMin: -1, xMax: 7, yMin: -4, yMax: 4 },
    minRank: 7,
  },
];

export const fnById = (id: FnId) => FUNCTIONS.find((f) => f.id === id) ?? FUNCTIONS[0]!;

/* ---------- valeurs exactes ---------- */

function gcd(a: number, b: number): number {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b) [a, b] = [b, a % b];
  return a || 1;
}

/** v rationnel (dénominateur ≤ 2 000) ? « 3/4 », « −0,5 », « 10/7 », « −0,299 » ; null sinon */
export function ratText(v: number): string | null {
  if (!Number.isFinite(v)) return null;
  for (let q = 1; q <= 2000; q++) {
    const p = Math.round(v * q);
    if (Math.abs(v * q - p) < 1e-10 * Math.max(1, Math.abs(p))) {
      const g = gcd(p, q);
      const P = p / g;
      const Q = q / g;
      // dénominateur 2ⁱ5ʲ : écriture décimale
      let r = Q;
      while (r % 2 === 0) r /= 2;
      while (r % 5 === 0) r /= 5;
      if (r === 1) return formatNumber(P / Q, 6).replace('-', '−');
      return `${P < 0 ? '−' : ''}${Math.abs(P)}/${Q}`;
    }
  }
  return null;
}

export interface Value {
  text: string;
  exact: boolean;
  v: number;
}

const approx = (v: number): Value => ({
  text: formatNumber(v, 3).replace('-', '−'),
  exact: false,
  v,
});

const exactOr = (v: number, special?: string | null): Value => {
  if (special) return { text: special, exact: true, v };
  const r = ratText(v);
  return r ? { text: r, exact: true, v } : approx(v);
};

/** √a écrit exactement quand a est un carré (de rationnel) : √(9/4) = 3/2 */
function sqrtText(a: number): string | null {
  const r = Math.sqrt(a);
  return ratText(r);
}

/** eᵃ : « 1 », « e », « e² », « e⁻¹ », « e^(0,5) » */
function expText(a: number): string | null {
  if (a === 0) return '1';
  if (a === 1) return 'e';
  if (Number.isInteger(a)) return `e${superscript(a)}`;
  const r = ratText(a);
  return r ? `e^(${r})` : null;
}

/** f(a), exact quand c'est possible */
export function valueAt(fn: Fn, a: number): Value {
  const v = fn.f(a);
  switch (fn.id) {
    case 'sqrt':
      return exactOr(v, sqrtText(a) ?? (ratText(a) ? `√${ratText(a)}` : null));
    case 'exp':
      return exactOr(v, expText(a));
    case 'ln':
      return exactOr(v, a === 1 ? '0' : ratText(a) ? `ln ${ratText(a)}` : null);
    default:
      return exactOr(v);
  }
}

/** f′(a), exact quand c'est possible */
export function slopeAt(fn: Fn, a: number): Value {
  const v = fn.d(a);
  switch (fn.id) {
    case 'sqrt': {
      const s = sqrtText(a);
      // 1/(2√a) : rationnel si a est un carré
      if (s) return exactOr(v);
      const r = ratText(a);
      return r ? { text: `1/(2√${r})`, exact: true, v } : approx(v);
    }
    case 'exp':
      return valueAt(fn, a);
    case 'ln':
      return exactOr(v);
    default:
      return exactOr(v);
  }
}

/* ---------- sécante, tangente ---------- */

/** taux d'accroissement entre a et a + h */
export const rate = (fn: Fn, a: number, h: number) => (fn.f(a + h) - fn.f(a)) / h;

/** les h proposés : de 2 à 0,001 */
export const H_STEPS = [2, 1, 0.5, 0.2, 0.1, 0.01, 0.001] as const;

/** h utilisable : B dans le domaine, de l'autre côté d'aucune valeur interdite */
export function hOk(fn: Fn, a: number, h: number): boolean {
  const b = a + h;
  if (!Number.isFinite(fn.f(b))) return false;
  if (fn.id === 'inverse' && a * b <= 0) return false;
  return true;
}

/**
 * Côté de B (h > 0 ou h < 0), le même pour tous les h : celui où B reste dans
 * le domaine (1/x : la même branche) et dans la figure pour le plus grand h.
 */
export function hSide(fn: Fn, a: number): 1 | -1 {
  const w = fn.window;
  const visible = (b: number) => {
    const y = fn.f(b);
    return b >= w.xMin && b <= w.xMax && Number.isFinite(y) && y >= w.yMin && y <= w.yMax;
  };
  for (const h of H_STEPS)
    for (const sd of [1, -1] as const) if (hOk(fn, a, sd * h) && visible(a + sd * h)) return sd;
  return hOk(fn, a, 0.001) ? 1 : -1;
}

/** « 3x », « −x », « x/9 », « −4x/9 », « 2,5x » : un coefficient devant x, sans ambiguïté */
function xTerm(m: number, t: string): string {
  if (t === '1') return 'x';
  if (t === '−1') return '−x';
  const frac = /^(−?)(\d+)\/(\d+)$/.exec(t);
  if (frac) return `${frac[1]}${frac[2] === '1' ? '' : frac[2]}x/${frac[3]}`;
  return `${t}x`;
}

/** équation réduite numérique : « y = 2x − 1 », « y ≈ 0,368x + 0,736 » */
function numericTangent(m: number, p: number): string {
  const mt = ratText(m);
  const pt = ratText(p);
  const exact = mt !== null && pt !== null;
  const t = (v: number, r: string | null) => r ?? formatNumber(v, 3).replace('-', '−');
  let s: string;
  if (Math.abs(m) < 1e-12) s = t(p, pt);
  else {
    const mx = xTerm(m, t(m, mt));
    const pa = t(Math.abs(p), ratText(Math.abs(p)));
    s = Math.abs(p) < 1e-12 ? mx : `${mx} ${p < 0 ? '−' : '+'} ${pa}`;
  }
  return `y ${exact ? '=' : '≈'} ${s}`;
}

/**
 * Équation de la tangente en A : y = f′(a)(x − a) + f(a), réduite, exacte
 * quand c'est possible — y compris avec e, ln et √ (« y = ex »,
 * « y = x/2 + ln 2 − 1 », « y = x/(2√2) + √2/2 »).
 */
export function tangentText(fn: Fn, a: number): string {
  const m = fn.d(a);
  const p = fn.f(a) - m * a;
  const ra = ratText(a);
  if (fn.id === 'exp' && a !== 0 && ra) {
    // y = eᵃ x + (1 − a) eᵃ
    const E = expText(a)!;
    const c = 1 - a;
    const ct = ratText(Math.abs(c));
    const cst = c === 0 ? '' : ` ${c < 0 ? '−' : '+'} ${ct === '1' ? '' : ct}${E}`;
    return `y = ${E}x${cst}`;
  }
  if (fn.id === 'ln' && a !== 1 && ra) {
    // y = x/a + ln a − 1
    return `y = ${xTerm(1 / a, ratText(1 / a)!)} + ln ${ra} − 1`;
  }
  if (fn.id === 'sqrt' && ra && ratText(Math.sqrt(a)) === null) {
    // y = x/(2√a) + √a/2
    return `y = x/(2√${ra}) + √${ra}/2`;
  }
  return numericTangent(m, p);
}

/** sens de variation au voisinage de a, lu sur le signe de f′(a) */
export function trend(fn: Fn, a: number): 'up' | 'down' | 'flat' {
  const m = fn.d(a);
  if (Math.abs(m) < 1e-9) return 'flat';
  return m > 0 ? 'up' : 'down';
}

/* ---------- défi : lire f′(a) sur la figure ---------- */

export interface ReadQuiz {
  fn: Fn;
  a: number;
  /** f′(a) */
  answer: number;
  options: Array<{ v: number; text: string; correct: boolean; mistake: ReadMistake | null }>;
}

export type ReadMistake = 'value' | 'inverse' | 'sign' | 'abscissa' | 'near';

export const READ_HINT: Record<ReadMistake, string> = {
  value: 'Ça, c’est f(a), l’ordonnée du point. f′(a) est la pente de la tangente.',
  inverse:
    'Pente = déplacement vertical ÷ déplacement horizontal, pas l’inverse : pour 1 vers la droite, de combien monte (ou descend) la tangente ?',
  sign: 'Regarde le sens : une tangente qui descend a une pente négative, qui monte une pente positive.',
  abscissa: 'Ça, c’est a, l’abscisse du point. f′(a) est la pente de la tangente.',
  near: 'Compte les carreaux le long de la tangente : pour 1 vers la droite, elle monte (ou descend) de f′(a).',
};

/**
 * Points où la pente se lit bien : la tangente passe par des nœuds du
 * quadrillage, A et le triangle de pente restent dans la figure, et aucune
 * erreur classique (f(a), a, −f′(a), 1/f′(a)) ne tombe sur la bonne réponse.
 */
export const READABLE: ReadonlyArray<readonly [FnId, number]> = [
  ['square', -2],
  ['square', -1],
  ['square', 1],
  ['cubic', -1],
  ['cubic', 0],
  ['cubic', 1],
  ['inverse', 1],
  ['inverse', 0.5],
  ['inverse', -0.5],
  ['sqrt', 1],
  ['sqrt', 4],
  ['exp', 0],
  ['ln', 1],
];

function shuffle<T>(list: readonly T[], rnd: () => number): T[] {
  const a = [...list];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rnd() * (i + 1));
    [a[i], a[j]] = [a[j]!, a[i]!];
  }
  return a;
}

/** nœuds entiers du quadrillage par lesquels passe la tangente, dans la fenêtre */
export function tangentNodes(fn: Fn, a: number): Array<[number, number]> {
  const m = fn.d(a);
  const p = fn.f(a) - m * a;
  const out: Array<[number, number]> = [];
  const { xMin, xMax, yMin, yMax } = fn.window;
  for (let x = Math.ceil(xMin); x <= Math.floor(xMax); x++) {
    const y = m * x + p;
    if (Math.abs(y - Math.round(y)) < 1e-9 && y >= yMin && y <= yMax) out.push([x, Math.round(y)]);
  }
  return out;
}

export function makeReadQuiz(rank: number, rnd: () => number, prev?: ReadQuiz): ReadQuiz {
  const list = READABLE.filter(
    ([id, a]) => fnById(id).minRank <= rank && !(prev && prev.fn.id === id && prev.a === a),
  );
  const [id, a] = list[Math.floor(rnd() * list.length)]!;
  const fn = fnById(id);
  const answer = fn.d(a);
  const text = (v: number) => ratText(v) ?? formatNumber(v, 2).replace('-', '−');
  // ordre fixe : une valeur partagée par deux erreurs garde l'explication la plus parlante
  const cands: Array<{ v: number; mistake: ReadMistake }> = [
    { v: -answer, mistake: 'sign' },
    { v: answer === 0 ? Infinity : 1 / answer, mistake: 'inverse' },
    { v: fn.f(a), mistake: 'value' },
    { v: a, mistake: 'abscissa' },
  ];
  const seen = new Set([text(answer)]);
  const opts: ReadQuiz['options'] = [
    { v: answer, text: text(answer), correct: true, mistake: null },
  ];
  for (const c of cands) {
    if (!Number.isFinite(c.v)) continue;
    const t = text(c.v);
    if (seen.has(t) || opts.length >= 4) continue;
    seen.add(t);
    opts.push({ v: c.v, text: t, correct: false, mistake: c.mistake });
  }
  // compléter avec des pentes voisines si deux erreurs se confondent
  for (const dv of [1, -1, 2, -2, 0.5]) {
    if (opts.length >= 4) break;
    const t = text(answer + dv);
    if (seen.has(t)) continue;
    seen.add(t);
    opts.push({ v: answer + dv, text: t, correct: false, mistake: 'near' });
  }
  return { fn, a, answer, options: shuffle(opts, rnd) };
}
